import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN } from '../../config/config';
import { createGeoJSONCircle, createGeoJSONPolygon } from './geoUtils';

import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

/*
 * Read-only Mapbox preview that paints up to two service areas (origin in
 * green, destination in red) using their `geographic_definition`. Each area
 * may be either a polygon or a radius circle around `center`.
 *
 * `originGeo` and `destGeo` shape:
 *   { type: 'radius' | 'polygon', center?, radius_km?, polygon_coordinates?, name }
 *
 * `active` gates map mounting so the preview can live behind a guard
 * (e.g. only render after the parent modal finished its enter transition).
 */
function ServiceAreaPreviewMap({
  originGeo,
  destGeo,
  active = true,
  mapHeight = '350px',
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    if (!active || !mapContainer.current) return undefined;

    // Always rebuild on prop change — the dataset is small and rebuilding
    // sidesteps stale-layer bugs.
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    if (!originGeo && !destGeo) return undefined;

    const bounds = new mapboxgl.LngLatBounds();
    const extendFromGeo = (geo) => {
      if (!geo) return;
      if (geo.type === 'polygon' && geo.polygon_coordinates?.length > 0) {
        geo.polygon_coordinates.forEach((coord) => {
          bounds.extend([coord.longitude, coord.latitude]);
        });
      } else if (geo.center) {
        bounds.extend([geo.center.longitude, geo.center.latitude]);
      }
    };
    extendFromGeo(originGeo);
    extendFromGeo(destGeo);

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: bounds.isEmpty() ? [19.0402, 47.4979] : bounds.getCenter(),
      zoom: 8,
    });
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      const renderArea = (geo, sourceId, color) => {
        if (!geo) return;
        let geoJSON;
        if (geo.type === 'polygon' && geo.polygon_coordinates?.length >= 3) {
          geoJSON = createGeoJSONPolygon(geo.polygon_coordinates);
        } else if (geo.center) {
          geoJSON = createGeoJSONCircle(
            [geo.center.longitude, geo.center.latitude],
            geo.radius_km || 2,
          );
        }
        if (!geoJSON) return;

        map.current.addSource(sourceId, { type: 'geojson', data: geoJSON });
        map.current.addLayer({
          id: `${sourceId}-fill`,
          type: 'fill',
          source: sourceId,
          paint: { 'fill-color': color, 'fill-opacity': 0.25 },
        });
        map.current.addLayer({
          id: `${sourceId}-outline`,
          type: 'line',
          source: sourceId,
          paint: { 'line-color': color, 'line-width': 2 },
        });

        if (geo.center) {
          const el = document.createElement('div');
          el.innerHTML = `<i class="fa fa-map-marker-alt" style="color: ${color}; font-size: 24px;"></i>`;
          const popupHtml = sourceId === 'origin-area'
            ? `<strong>Origin:</strong> ${geo.name}`
            : `<strong>Destination:</strong> ${geo.name}`;
          new mapboxgl.Marker({ element: el })
            .setLngLat([geo.center.longitude, geo.center.latitude])
            .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupHtml))
            .addTo(map.current);
        }
      };

      renderArea(originGeo, 'origin-area', '#28a745');
      renderArea(destGeo, 'destination-area', '#dc3545');

      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, { padding: 50 });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [active, originGeo, destGeo]);

  return (
    <div
      ref={mapContainer}
      style={{ height: mapHeight, width: '100%' }}
    />
  );
}

export default ServiceAreaPreviewMap;
